import { create } from 'zustand';

export type SolubilityStage =
  | 'setup'
  | 'water-poured'
  | 'salt-weighed'
  | 'salt-added'
  | 'heating'
  | 'stirring'
  | 'observed'
  | 'completed';

export type TrialId = 1 | 2 | 3;
export type CameraView = 'default' | 'top' | 'side' | 'closeup' | 'workbench';

export interface TrialPreset {
  id: TrialId;
  targetTempC: number;
  startTempC: number;
  needsHeating: boolean;
}

export const TRIAL_PRESETS: Record<TrialId, TrialPreset> = {
  1: { id: 1, targetTempC: 10, startTempC: 10, needsHeating: false },
  2: { id: 2, targetTempC: 30, startTempC: 25, needsHeating: true },
  3: { id: 3, targetTempC: 80, startTempC: 25, needsHeating: true },
};

export interface TrialResult {
  trialId: TrialId;
  targetTempC: number;
  finalTempC: number;
  saltMassG: number;
  dissolvedFractionFinal: number;
  dissolvedMassG: number;
  undissolvedMassG: number;
  observation: string;
  recordedAt: number;
}

interface SolubilityState {
  currentTrial: TrialId;
  labStage: SolubilityStage;

  waterVolumeMl: number;
  waterTempC: number;
  saltOnScaleG: number;
  saltInBeakerG: number;
  dissolvedFraction: number;

  isHeating: boolean;
  heatIntensity: number;
  isStirring: boolean;
  stirSeconds: number;

  isPouringWater: boolean;
  pourProgress: number;
  isTransferringSalt: boolean;
  transferProgress: number;

  experimentTime: number;
  results: TrialResult[];

  simulationSpeed: number;
  cameraView: CameraView;
  cameraVersion: number;
  isAutoDemoRunning: boolean;
  showTutorial: boolean;

  selectTrial: (id: TrialId) => void;
  pourWater: () => void;
  weighSaltStep: () => void;
  addSaltToBeaker: () => void;
  toggleHeating: () => void;
  setHeatIntensity: (i: number) => void;
  setStirring: (active: boolean) => void;
  recordObservation: () => void;
  resetTrial: () => void;
  resetAll: () => void;
  setSimulationSpeed: (s: number) => void;
  cycleSimulationSpeed: () => void;
  setCameraView: (v: CameraView) => void;
  setAutoDemoRunning: (running: boolean) => void;
  setShowTutorial: (show: boolean) => void;
  tick: (delta: number) => void;
}

const SPEED_CYCLE = [1, 2, 5, 10];

const TARGET_STIR_SECONDS = 120;
const SALT_TARGET_MASS_G = 40;
const WATER_VOL_ML = 50;

function solubilityFractionFor(tempC: number): number {
  const f = (tempC - 5) / 75;
  return Math.max(0.30, Math.min(1.0, f));
}

export const useSolubilityStore = create<SolubilityState>((set, get) => ({
  currentTrial: 1,
  labStage: 'setup',
  waterVolumeMl: 0,
  waterTempC: 25,
  saltOnScaleG: 0,
  saltInBeakerG: 0,
  dissolvedFraction: 0,
  isHeating: false,
  heatIntensity: 0.6,
  isStirring: false,
  stirSeconds: 0,
  isPouringWater: false,
  pourProgress: 0,
  isTransferringSalt: false,
  transferProgress: 0,
  experimentTime: 0,
  results: [],
  simulationSpeed: 1,
  cameraView: 'default',
  cameraVersion: 0,
  isAutoDemoRunning: false,
  showTutorial: false,

  selectTrial: (id) => {
    const preset = TRIAL_PRESETS[id];
    set({
      currentTrial: id,
      labStage: 'setup',
      waterVolumeMl: 0,
      waterTempC: preset.startTempC,
      saltOnScaleG: 0,
      saltInBeakerG: 0,
      dissolvedFraction: 0,
      isHeating: false,
      isStirring: false,
      stirSeconds: 0,
      isPouringWater: false,
      pourProgress: 0,
      isTransferringSalt: false,
      transferProgress: 0,
      experimentTime: 0,
    });
  },

  pourWater: () => set((s) => {
    if (s.isPouringWater || s.labStage !== 'setup') return {};
    return { isPouringWater: true, pourProgress: 0, waterVolumeMl: 0 };
  }),

  weighSaltStep: () => set((s) => {
    const next = Math.min(SALT_TARGET_MASS_G, s.saltOnScaleG + 5);
    return {
      saltOnScaleG: next,
      labStage: next >= SALT_TARGET_MASS_G ? 'salt-weighed' : s.labStage,
    };
  }),

  addSaltToBeaker: () => set((s) => {
    if (s.saltOnScaleG < SALT_TARGET_MASS_G || s.isTransferringSalt) return {};
    return {
      isTransferringSalt: true,
      transferProgress: 0,
      saltInBeakerG: 0,
    };
  }),

  toggleHeating: () => set((s) => ({ isHeating: !s.isHeating })),
  setHeatIntensity: (i) => set({ heatIntensity: Math.max(0, Math.min(1, i)) }),

  setStirring: (active) => set((s) => ({
    isStirring: active,
    labStage: active && (s.labStage === 'heating' || s.labStage === 'salt-added')
      ? 'stirring'
      : s.labStage,
  })),

  recordObservation: () => set((s) => {
    const fractionFinal = s.dissolvedFraction;
    const dissolvedMass = SALT_TARGET_MASS_G * fractionFinal;
    const undissolved = SALT_TARGET_MASS_G - dissolvedMass;
    const obs = fractionFinal >= 0.99
      ? 'All salt dissolved; clear solution.'
      : fractionFinal >= 0.55
        ? 'Most salt dissolved; small amount visible at bottom.'
        : 'Only some salt dissolved; lots of undissolved crystals at bottom.';
    const result: TrialResult = {
      trialId: s.currentTrial,
      targetTempC: TRIAL_PRESETS[s.currentTrial].targetTempC,
      finalTempC: parseFloat(s.waterTempC.toFixed(1)),
      saltMassG: SALT_TARGET_MASS_G,
      dissolvedFractionFinal: parseFloat(fractionFinal.toFixed(2)),
      dissolvedMassG: parseFloat(dissolvedMass.toFixed(1)),
      undissolvedMassG: parseFloat(undissolved.toFixed(1)),
      observation: obs,
      recordedAt: Math.floor(s.experimentTime),
    };
    const allResults = [...s.results.filter(r => r.trialId !== s.currentTrial), result];
    const completed = allResults.length === 3;
    return {
      results: allResults,
      labStage: completed ? 'completed' : 'observed',
    };
  }),

  resetTrial: () => {
    const id = get().currentTrial;
    get().selectTrial(id);
  },

  resetAll: () => set({
    currentTrial: 1,
    labStage: 'setup',
    waterVolumeMl: 0,
    waterTempC: 25,
    saltOnScaleG: 0,
    saltInBeakerG: 0,
    dissolvedFraction: 0,
    isHeating: false,
    heatIntensity: 0.6,
    isStirring: false,
    stirSeconds: 0,
    isPouringWater: false,
    pourProgress: 0,
    isTransferringSalt: false,
    transferProgress: 0,
    experimentTime: 0,
    results: [],
    simulationSpeed: 1,
  }),

  setSimulationSpeed: (s) => set({ simulationSpeed: Math.max(1, Math.min(10, s)) }),

  cycleSimulationSpeed: () => set((state) => {
    const idx = SPEED_CYCLE.indexOf(state.simulationSpeed);
    const next = SPEED_CYCLE[(idx + 1) % SPEED_CYCLE.length];
    return { simulationSpeed: next };
  }),

  setCameraView: (v) => set((s) => ({ cameraView: v, cameraVersion: s.cameraVersion + 1 })),
  setAutoDemoRunning: (running) => set({ isAutoDemoRunning: running }),
  setShowTutorial: (show) => set({ showTutorial: show }),

  tick: (delta) => set((s) => {
    // Pour-water animation runs in real-time, independent of simulationSpeed,
    // so the user always sees it clearly even at 10× sim speed.
    const realDelta = delta / Math.max(0.0001, s.simulationSpeed);

    if (s.isPouringWater) {
      const POUR_DURATION = 2.4;
      const newProgress = Math.min(1, s.pourProgress + realDelta / POUR_DURATION);
      if (newProgress >= 1) {
        return {
          isPouringWater: false,
          pourProgress: 1,
          waterVolumeMl: WATER_VOL_ML,
          labStage: 'water-poured',
        };
      }
      // Phase 1 (0..0.2): cylinder lifts and tilts over the beaker — no water yet
      // Phase 2 (0.2..0.8): cylinder drains into the beaker
      // Phase 3 (0.8..1.0): cylinder returns to its workbench rest position
      const beakerFill =
        newProgress < 0.2 ? 0 :
        newProgress < 0.8 ? WATER_VOL_ML * ((newProgress - 0.2) / 0.6) :
        WATER_VOL_ML;
      return {
        pourProgress: newProgress,
        waterVolumeMl: beakerFill,
      };
    }

    if (s.isTransferringSalt) {
      const TRANSFER_DURATION = 2.0;
      const newProgress = Math.min(1, s.transferProgress + realDelta / TRANSFER_DURATION);
      if (newProgress >= 1) {
        return {
          isTransferringSalt: false,
          transferProgress: 1,
          saltOnScaleG: 0,
          saltInBeakerG: SALT_TARGET_MASS_G,
          labStage: TRIAL_PRESETS[s.currentTrial].needsHeating ? 'heating' : 'stirring',
        };
      }
      // Salt leaves scale across the full transfer; beaker only fills as the clump
      // crosses into it (second half of the arc).
      const beakerSalt =
        newProgress < 0.5 ? 0 :
        SALT_TARGET_MASS_G * ((newProgress - 0.5) / 0.5);
      return {
        transferProgress: newProgress,
        saltOnScaleG: SALT_TARGET_MASS_G * (1 - newProgress),
        saltInBeakerG: beakerSalt,
      };
    }

    if (s.labStage === 'setup' || s.labStage === 'completed' || s.labStage === 'observed') return {};

    const newTime = s.experimentTime + delta;

    let nextWaterTemp = s.waterTempC;
    if (s.isHeating) {
      const target = 25 + s.heatIntensity * 100;
      nextWaterTemp += (target - s.waterTempC) * 0.06 * delta;
      if (nextWaterTemp > 100) nextWaterTemp = 100;
    } else if (!s.isStirring && nextWaterTemp > TRIAL_PRESETS[s.currentTrial].startTempC) {
      // Water cools toward start temp ONLY when not actively stirring.
      // While stirring we treat the bath as held at temperature so the recorded
      // reading matches the trial target instead of drifting downward.
      nextWaterTemp -= (nextWaterTemp - TRIAL_PRESETS[s.currentTrial].startTempC) * 0.005 * delta;
    }

    let nextStir = s.stirSeconds;
    let nextDissolved = s.dissolvedFraction;
    if (s.isStirring && s.saltInBeakerG > 0) {
      nextStir += delta;
      const targetFraction = solubilityFractionFor(nextWaterTemp);
      const stirProgress = Math.min(1, nextStir / TARGET_STIR_SECONDS);
      nextDissolved = targetFraction * stirProgress;
    }

    return {
      experimentTime: newTime,
      waterTempC: nextWaterTemp,
      stirSeconds: nextStir,
      dissolvedFraction: nextDissolved,
    };
  }),
}));
